import { c as createColorGetterSpectrum, g as getTreeMaxDepth, u as useWheelEventListener, a as useResizeEventListener, n as now, b as colorToCanvasFill, s as strokeRectWithFirefoxBugWorkaround, d as bytesToText } from './shared/nanovis.D50MWeEo.mjs';
export { e as colorToCssBackground, f as createColorGetterFromMap, h as hueAngleToColor, i as normalizeTreeNode } from './shared/nanovis.D50MWeEo.mjs';

let createNanoEvents = () => ({
  emit(event, ...args) {
    for (
      let callbacks = this.events[event] || [],
        i = 0,
        length = callbacks.length;
      i < length;
      i++
    ) {
      callbacks[i](...args);
    }
  },
  events: {},
  on(event, cb) {
(this.events[event] ||= []).push(cb);
    return () => {
      this.events[event] = this.events[event]?.filter(i => cb !== i);
    }
  }
});

const DEFAULT_GRAPH_OPTIONS = {
  getText: (node) => node.text,
  getSubtext: (node) => node.subtext,
  animate: true,
  animateDuration: 350
};
const DEFAULT_PALETTE = {
  fallback: "#CCC",
  stroke: "#0008",
  text: "#222",
  hover: "#fff5",
  shadow: "#0008",
  fg: "#fff",
  bg: "#222"
};

const CONSTANT_DOT_CHAR_CODE = 46;
const CONSTANT_NORMAL_FONT = "14px sans-serif";
const CONSTANT_BOLD_FONT = `bold ${CONSTANT_NORMAL_FONT}`;
class GraphBase {
  el;
  canvas;
  c;
  width = 0;
  height = 0;
  ratio = 1;
  root;
  maxDepth;
  events;
  options;
  palette;
  disposables;
  getColor;
  getText;
  getSubtext;
  _animationFrame = null;
  constructor(tree, options) {
    this.options = {
      ...DEFAULT_GRAPH_OPTIONS,
      ...options
    };
    const {
      getColor = createColorGetterSpectrum(tree),
      getText = () => void 0,
      getSubtext = () => void 0
    } = this.options;
    this.palette = {
      ...DEFAULT_PALETTE,
      ...options.palette
    };
    this.el = document.createElement("div");
    this.canvas = document.createElement("canvas");
    this.c = this.canvas.getContext("2d");
    this.root = tree;
    this.maxDepth = getTreeMaxDepth(tree);
    this.disposables = [];
    this.events = createNanoEvents();
    this.getColor = getColor;
    this.getText = getText;
    this.getSubtext = getSubtext;
    this.setFont(CONSTANT_NORMAL_FONT);
    if (options.onClick)
      this.events.on("click", options.onClick);
    if (options.onHover)
      this.events.on("hover", options.onHover);
    if (options.onLeave)
      this.events.on("leave", options.onLeave);
    if (options.onSelect)
      this.events.on("select", options.onSelect);
    this.el.addEventListener("mouseleave", () => {
      this.events.emit("leave");
    });
  }
  /**
   * Invalidate the graph and request a new frame.
   */
  invalidate() {
    if (this._animationFrame === null) {
      this._animationFrame = requestAnimationFrame(() => {
        this._animationFrame = null;
        this.tick();
      });
    }
  }
  /**
   * To be overridden by subclasses to implement custom animation logic.
   */
  tick() {
    this.draw();
  }
  /**
   * To be overridden by subclasses to implement custom animation logic.
   */
  draw() {
  }
  resize() {
    this.ratio = window.devicePixelRatio || 1;
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;
    this.canvas.width = Math.round(this.width * this.ratio);
    this.canvas.height = Math.round(this.height * this.ratio);
    this.c.scale(this.ratio, this.ratio);
    this.draw();
  }
  /**
   * Select a node. To be overridden by subclasses to implement custom selection logic.
   *
   * @param node - The node to select.
   * @param _animate - Whether to animate the selection.
   */
  select(node, _animate) {
    this.events.emit("select", node);
  }
  dispose() {
    this.disposables.forEach((disposable) => disposable());
    this.disposables.length = 0;
    this.el.remove();
  }
  [Symbol.dispose]() {
    this.dispose();
  }
  _font = "14px sans-serif";
  _fontWidthCache = /* @__PURE__ */ new Map();
  ellipsisWidth = 0;
  getFontCache() {
    if (!this._fontWidthCache.has(this._font))
      this._fontWidthCache.set(this._font, {});
    return this._fontWidthCache.get(this._font);
  }
  setFont(font) {
    this._font = font;
    this.c.font = font;
    this.c.textBaseline = "middle";
    this.ellipsisWidth = 3 * this.charCodeWidth(CONSTANT_DOT_CHAR_CODE);
  }
  charCodeWidth(ch) {
    const cache = this.getFontCache();
    let width = cache[ch];
    if (width === void 0) {
      width = this.c.measureText(String.fromCharCode(ch)).width;
      cache[ch] = width;
    }
    return width;
  }
  textOverflowEllipsis(text, width) {
    if (width < this.ellipsisWidth)
      return ["", 0];
    let textWidth = 0;
    const n = text.length;
    let i = 0;
    while (i < n) {
      const charWidth = this.charCodeWidth(text.charCodeAt(i));
      if (width < textWidth + this.ellipsisWidth + charWidth) {
        return [`${text.slice(0, i)}...`, textWidth + this.ellipsisWidth];
      }
      textWidth += charWidth;
      i++;
    }
    return [text, textWidth];
  }
}

const CONSTANT_ROW_HEIGHT = 24;
const CONSTANT_TEXT_INDENT = 5;
const CONSTANT_FOCUS_PADDING_RATIO = 0.01;
const CONSTANT_ZOOMED_OUT_WIDTH = 1e3;
class Flamegraph extends GraphBase {
  mainEl = document.createElement("div");
  totalBytes;
  viewportMin;
  viewportMax;
  zoomedOutMin = 0;
  zoomedOutWidth = 0;
  prevWheelTime = 0;
  prevWheelWasZoom = false;
  stripeScaleAdjust = 1;
  hoveredNode = null;
  selectedNode = null;
  nodeStartBytes = /* @__PURE__ */ new Map();
  animationFromMin = 0;
  animationFromMax = 0;
  animationToMin = 0;
  animationToMax = 0;
  animationStart = 0;
  constructor(root, userOptions = {}) {
    super(root, userOptions);
    this.totalBytes = root.size;
    this.viewportMin = this.animationFromMin = this.animationToMin = 0;
    this.viewportMax = this.animationFromMax = this.animationToMax = this.totalBytes;
    Object.assign(this.mainEl.style, {
      position: "relative"
    });
    Object.assign(this.canvas.style, {
      position: "absolute",
      left: "0",
      top: "0"
    });
    let didDrag = false;
    this.canvas.onmousedown = (e) => {
      didDrag = false;
      if (e.button !== 2) {
        let oldX = e.pageX;
        const move = (e2) => {
          const deltaX = e2.pageX - oldX;
          if (!didDrag && Math.abs(deltaX) < 3)
            return;
          didDrag = true;
          this.modifyViewport(-deltaX, 0, null);
          oldX = e2.pageX;
        };
        const up = () => {
          document.removeEventListener("mousemove", move);
          document.removeEventListener("mouseup", up);
        };
        e.preventDefault();
        document.addEventListener("mousemove", move);
        document.addEventListener("mouseup", up);
      }
    };
    this.canvas.onmousemove = (e) => {
      this.updateHover(e);
    };
    this.canvas.onmouseout = () => {
      this.changeHoveredNode(null);
    };
    this.canvas.onclick = (e) => {
      if (didDrag)
        return;
      const node = this.hitTestNode(e);
      this.changeHoveredNode(node);
      this.changeSelectedNode(node);
      if (node)
        this.events.emit("click", node, e);
    };
    this.disposables.push(useWheelEventListener((e) => {
      const deltaX = e.deltaX;
      const deltaY = e.deltaY;
      const wheelTime = now();
      const isZoom = wheelTime - this.prevWheelTime < 50 ? this.prevWheelWasZoom : e.ctrlKey || e.metaKey;
      this.prevWheelTime = wheelTime;
      this.prevWheelWasZoom = isZoom;
      if (isZoom || Math.abs(deltaX) >= Math.abs(deltaY)) {
        e.preventDefault();
      }
      this.modifyViewport(deltaX, deltaY, isZoom ? e.pageX : null);
      this.updateHover(e);
    }));
    this.resize();
    Promise.resolve().then(() => this.resize());
    this.disposables.push(useResizeEventListener(() => this.resize()));
    this.mainEl.append(this.canvas);
    this.el.append(this.mainEl);
  }
  tick() {
    let t = (now() - this.animationStart) / (this.options.animateDuration ?? DEFAULT_GRAPH_OPTIONS.animateDuration);
    if (t < 0 || t > 1) {
      t = 1;
      this.viewportMin = this.animationToMin;
      this.viewportMax = this.animationToMax;
    } else {
      if (t < 0.5) {
        t *= 4 * t * t;
      } else {
        t = 1 - t;
        t *= 4 * t * t;
        t = 1 - t;
      }
      this.viewportMin = this.animationFromMin + (this.animationToMin - this.animationFromMin) * t;
      this.viewportMax = this.animationFromMax + (this.animationToMax - this.animationFromMax) * t;
      this.invalidate();
    }
    this.draw();
  }
  draw() {
    this.c.clearRect(0, 0, this.width, this.height);
    this.drawNode(this.root, 0, 0, -Infinity, 1 /* ROOT */);
  }
  changeSelectedNode(node, animate = this.options.animate ?? DEFAULT_GRAPH_OPTIONS.animate) {
    if (this.selectedNode === node)
      return;
    this.selectedNode = node;
    this.events.emit("select", node);
    if (node && this.nodeStartBytes.has(node.id)) {
      let start = this.nodeStartBytes.get(node.id);
      let end = start + node.size;
      start = Math.max(0, start - node.size * CONSTANT_FOCUS_PADDING_RATIO);
      end = Math.min(this.totalBytes, end + node.size * CONSTANT_FOCUS_PADDING_RATIO);
      this.setViewport(start, end, animate);
    }
  }
  select(node, animate = this.options.animate ?? DEFAULT_GRAPH_OPTIONS.animate) {
    this.changeSelectedNode(node, animate);
  }
  resize() {
    this.width = this.el.clientWidth;
    this.height = this.maxDepth * CONSTANT_ROW_HEIGHT + 1;
    this.zoomedOutMin = this.width - CONSTANT_ZOOMED_OUT_WIDTH >> 1;
    this.zoomedOutWidth = this.zoomedOutMin + CONSTANT_ZOOMED_OUT_WIDTH;
    if (this.zoomedOutMin < 0)
      this.zoomedOutMin = 0;
    if (this.zoomedOutWidth > this.width)
      this.zoomedOutWidth = this.width;
    this.zoomedOutWidth -= this.zoomedOutMin;
    this.stripeScaleAdjust = this.totalBytes / this.zoomedOutWidth;
    this.mainEl.style.height = `${this.height}px`;
    super.resize();
  }
  // We want to avoid overlapping strokes from lots of really small adjacent
  // rectangles all merging together into a solid color. So we enforce a
  // minimum rectangle width of 2px and we also skip drawing rectangles that
  // have a right edge less than 1.5px from the previous right edge.
  drawNode(node, y, startBytes, prevRightEdge, flags) {
    this.nodeStartBytes.set(node.id, startBytes);
    const scale = this.zoomedOutWidth / (this.viewportMax - this.viewportMin);
    const x = this.zoomedOutMin + (startBytes - this.viewportMin) * scale;
    const w = node.size * scale;
    const rightEdge = x + w;
    if (rightEdge < prevRightEdge + 1.5)
      return prevRightEdge;
    if (x + w < 0 || x > this.width)
      return rightEdge;
    const rectWidth = w < 2 ? 2 : w;
    const textX = (x > 0 ? x : 0) + CONSTANT_TEXT_INDENT;
    const textY = y + CONSTANT_ROW_HEIGHT / 2;
    let text = this.getText(node) || "";
    let subText = this.getSubtext(node) || "";
    let measuredW;
    let typesetX = 0;
    const typesetW = w + x - textX;
    const fillColor = colorToCanvasFill(this.getColor(node) || this.palette.fallback, this.c, this.zoomedOutMin - this.viewportMin * scale, CONSTANT_ROW_HEIGHT, scale * this.stripeScaleAdjust);
    let textColor = this.palette.text;
    let childRightEdge = -Infinity;
    if (flags & 1 /* ROOT */) {
      textColor = this.palette.fg;
      this.setFont(CONSTANT_BOLD_FONT);
    } else {
      this.c.fillStyle = fillColor;
      this.c.fillRect(x, y, rectWidth, CONSTANT_ROW_HEIGHT);
      if (flags & 2 /* HOVER */ || this.hoveredNode && node.id === this.hoveredNode.id) {
        this.c.fillStyle = this.palette.hover;
        this.c.fillRect(x, y, rectWidth, CONSTANT_ROW_HEIGHT);
        flags |= 2 /* HOVER */;
      }
    }
    if (this.ellipsisWidth < typesetW) {
      measuredW = this.c.measureText(text).width;
      if (measuredW <= typesetW) {
        typesetX += measuredW;
      } else {
        text = this.textOverflowEllipsis(text, typesetW)[0];
        typesetX = typesetW;
      }
      this.c.fillStyle = textColor;
      this.c.fillText(text, textX, textY);
    }
    if (flags & 1 /* ROOT */) {
      this.setFont(CONSTANT_NORMAL_FONT);
    }
    if (typesetX + this.ellipsisWidth < typesetW) {
      if (subText && text)
        subText = ` - ${subText}`;
      measuredW = this.c.measureText(subText).width;
      if (typesetX + measuredW > typesetW) {
        subText = this.textOverflowEllipsis(subText, typesetW - typesetX)[0];
      }
      this.c.globalAlpha = 0.5;
      this.c.fillText(subText, textX + typesetX, textY);
      this.c.globalAlpha = 1;
    }
    for (const child of node.children) {
      childRightEdge = this.drawNode(child, y + CONSTANT_ROW_HEIGHT, startBytes, childRightEdge, flags & -2 /* ROOT */);
      startBytes += child.size;
    }
    if (!(flags & 1 /* ROOT */)) {
      strokeRectWithFirefoxBugWorkaround(this.c, this.palette.stroke, x + 0.5, y + 0.5, rectWidth, CONSTANT_ROW_HEIGHT);
    }
    return rightEdge;
  }
  changeHoveredNode(node) {
    if (this.hoveredNode !== node) {
      this.hoveredNode = node;
      this.canvas.style.cursor = node && !node.children.length ? "pointer" : "auto";
      this.invalidate();
    }
  }
  hitTestNode(mouseEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const mouseX = mouseEvent.clientX - rect.left;
    const mouseY = mouseEvent.clientY - rect.top;
    const mouseBytes = this.viewportMin + (this.viewportMax - this.viewportMin) / this.zoomedOutWidth * (mouseX - this.zoomedOutMin);
    const visit = (node, y, startBytes) => {
      if (mouseBytes >= startBytes && mouseBytes < startBytes + node.size) {
        if (mouseY >= y && mouseY < y + CONSTANT_ROW_HEIGHT && node.id) {
          return node;
        }
        if (mouseY >= y + CONSTANT_ROW_HEIGHT) {
          for (const child of node.children) {
            const result = visit(child, y + CONSTANT_ROW_HEIGHT, startBytes);
            if (result)
              return result;
            startBytes += child.size;
          }
        }
      }
      return null;
    };
    return visit(this.root, 0, 0);
  }
  modifyViewport(deltaX, deltaY, xForZoom) {
    let min = this.viewportMin;
    let max = this.viewportMax;
    let translate = 0;
    if (xForZoom !== null) {
      const mouse = min + (max - min) / this.zoomedOutWidth * (xForZoom - this.zoomedOutMin);
      const scale = 1.01 ** deltaY;
      min = mouse + (min - mouse) * scale;
      max = mouse + (max - mouse) * scale;
    } else {
      translate = deltaX * (max - min) / this.zoomedOutWidth;
    }
    if (min + translate < 0)
      translate = -min;
    else if (max + translate > this.totalBytes)
      translate = this.totalBytes - max;
    min += translate;
    max += translate;
    if (min < 0)
      min = 0;
    if (max > this.totalBytes)
      max = this.totalBytes;
    this.setViewport(min, max, false);
  }
  setViewport(min, max, animate) {
    this.animationFromMin = this.viewportMin;
    this.animationFromMax = this.viewportMax;
    this.animationToMin = min;
    this.animationToMax = max;
    if (animate)
      this.animationStart = now();
    else
      this.animationStart = 0;
    this.invalidate();
  }
  updateHover(e) {
    const node = this.hitTestNode(e);
    this.changeHoveredNode(node);
    this.events.emit("hover", node, e);
  }
}

function isParentOf(parent, child) {
  while (child) {
    if (child === parent)
      return true;
    child = child.parent;
  }
  return false;
}
function narrowSlice(root, node, slice) {
  if (root === node)
    return;
  const parent = node.parent;
  const totalBytes = parent.size || 1;
  let bytesSoFar = 0;
  narrowSlice(root, parent, slice);
  for (const child of parent.children) {
    if (child === node) {
      slice.startAngle_ += slice.sweepAngle_ * bytesSoFar / totalBytes;
      slice.sweepAngle_ = child.size / totalBytes * slice.sweepAngle_;
      break;
    }
    bytesSoFar += child.size;
  }
  slice.depth_ += 1;
}
const baseRadiusCache = {};
function baseRadius(depth) {
  if (!baseRadiusCache[depth])
    baseRadiusCache[depth] = 50 * 8 * Math.log(1 + Math.log(1 + depth / 8));
  return baseRadiusCache[depth];
}
const START_ANGLE = -Math.PI / 2;
class Sunburst extends GraphBase {
  currentNode;
  hoveredNode;
  centerX = 0;
  centerY = 0;
  animationStart = 0;
  radiusScale = 1;
  sourceDepth = 0;
  sourceStartAngle = START_ANGLE;
  sourceSweepAngle = Math.PI * 2;
  targetNode;
  targetDepth = this.sourceDepth;
  targetStartAngle = this.sourceStartAngle;
  targetSweepAngle = this.sourceSweepAngle;
  animatedNode;
  animatedDepth = this.sourceDepth;
  animatedStartAngle = this.sourceStartAngle;
  animatedSweepAngle = this.sourceSweepAngle;
  previousHoveredNode;
  historyStack = [];
  constructor(tree, options = {}) {
    while (tree.children.length === 1) {
      tree = tree.children[0];
    }
    super(tree, options);
    this.currentNode = tree;
    this.targetNode = this.currentNode;
    this.animatedNode = this.currentNode;
    this.canvas.onmousemove = (e) => {
      this.handleMouseMove(e);
    };
    this.canvas.onmouseout = (e) => {
      this.changeHoveredNode(void 0);
      this.events.emit("hover", null, e);
    };
    this.canvas.onclick = (e) => {
      let node = this.hitTestNode(e);
      if (!node)
        return;
      this.events.emit("click", node, e);
      let stack = [];
      if (node !== this.animatedNode.parent) {
        stack = this.historyStack.concat(this.currentNode);
      } else if (this.historyStack.length > 0) {
        node = this.historyStack.pop();
        stack = this.historyStack.slice();
      }
      this.events.emit("click", node, e);
      if (node.children.length > 0) {
        this.select(node);
        this.historyStack = stack;
      } else {
        e.preventDefault();
      }
    };
    this.el.append(this.canvas);
    this.resize();
    Promise.resolve().then(() => this.resize());
    this.disposables.push(useResizeEventListener(() => this.resize()));
    this.disposables.push(useWheelEventListener((e) => this.handleMouseMove(e)));
  }
  select(node, animate) {
    if (this.currentNode !== node || this.root) {
      this.currentNode = node || this.root;
      this.updateSunburst(animate);
      this.events.emit("select", node);
    }
  }
  resize() {
    this.width = this.el.clientWidth;
    this.height = this.width;
    this.centerX = this.width >> 1;
    this.centerY = this.height >> 1;
    const maxRadius = 2 * Math.ceil(baseRadius(this.maxDepth));
    this.radiusScale = this.width / maxRadius;
    super.resize();
  }
  draw() {
    this.c.clearRect(0, 0, this.width, this.height);
    this.drawNode(this.animatedNode, this.animatedDepth, this.computeRadius(this.animatedDepth), this.animatedStartAngle, this.animatedSweepAngle, 1 /* ROOT */ | 2 /* FILL */, -Infinity);
    this.c.strokeStyle = this.palette.stroke;
    this.c.beginPath();
    this.drawNode(this.animatedNode, this.animatedDepth, this.computeRadius(this.animatedDepth), this.animatedStartAngle, this.animatedSweepAngle, 1 /* ROOT */, -Infinity);
    this.c.stroke();
    if (this.animatedDepth === 0) {
      this.c.fillStyle = this.palette.stroke;
      this.setFont(CONSTANT_BOLD_FONT);
      this.c.textAlign = "center";
      this.c.fillText(bytesToText(this.targetNode.size), this.centerX, this.centerY);
    }
  }
  // We want to avoid overlapping strokes from lots of really small adjacent
  // slices all merging together into a solid color. So we enforce a
  // minimum slice width of 2px and we also skip drawing slices that
  // have a tail edge less than 1.5px from the previous tail edge.
  drawNode(node, depth, innerRadius, startAngle, sweepAngle, flags, prevTailEdge) {
    const outerRadius = this.computeRadius(depth + 1);
    if (outerRadius > this.centerY)
      return prevTailEdge;
    if (node === this.hoveredNode) {
      flags |= 8 /* HOVER */;
    }
    const middleRadius = (innerRadius + outerRadius) / 2;
    const tailEdge = startAngle + sweepAngle;
    if (tailEdge - prevTailEdge < 1.5 / middleRadius)
      return prevTailEdge;
    let clampedSweepAngle = 2 / middleRadius;
    if (sweepAngle > clampedSweepAngle)
      clampedSweepAngle = sweepAngle;
    if (flags & 2 /* FILL */) {
      this.c.fillStyle = colorToCanvasFill(this.getColor(node) || this.palette.fallback, this.c, this.centerX, this.centerY, 1);
      this.c.beginPath();
      this.c.arc(this.centerX, this.centerY, innerRadius, startAngle, startAngle + clampedSweepAngle, false);
      this.c.arc(this.centerX, this.centerY, outerRadius, startAngle + clampedSweepAngle, startAngle, true);
      this.c.fill();
      if (this.hoveredNode && (flags & 8 /* HOVER */ || node.parent === this.hoveredNode)) {
        this.c.fillStyle = this.palette.hover;
        this.c.fill();
      }
    } else {
      const isFullCircle = clampedSweepAngle === Math.PI * 2;
      const moveToRadius = flags & 4 /* CHAIN */ || isFullCircle ? outerRadius : innerRadius;
      if (flags & 1 /* ROOT */ && innerRadius > 0)
        this.c.arc(this.centerX, this.centerY, innerRadius, startAngle + clampedSweepAngle, startAngle, true);
      this.c.moveTo(this.centerX + moveToRadius * Math.cos(startAngle), this.centerY + moveToRadius * Math.sin(startAngle));
      this.c.arc(this.centerX, this.centerY, outerRadius, startAngle, startAngle + clampedSweepAngle, false);
      if (!isFullCircle)
        this.c.lineTo(this.centerX + innerRadius * Math.cos(startAngle + clampedSweepAngle), this.centerY + innerRadius * Math.sin(startAngle + clampedSweepAngle));
    }
    const totalBytes = node.size;
    let childFlags = flags & (2 /* FILL */ | 8 /* HOVER */);
    let bytesSoFar = 0;
    let childTailEdge = -Infinity;
    for (const child of node.children) {
      childTailEdge = this.drawNode(child, depth + 1, outerRadius, startAngle + sweepAngle * bytesSoFar / totalBytes, child.size / totalBytes * sweepAngle, childFlags, childTailEdge);
      bytesSoFar += child.size;
      childFlags |= 4 /* CHAIN */;
    }
    return tailEdge;
  }
  changeHoveredNode(node, animate) {
    if (this.hoveredNode !== node) {
      this.hoveredNode = node;
      this.updateSunburst(animate);
    }
  }
  hitTestNode(mouseEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const x = mouseEvent.clientX - rect.left - this.centerX;
    const y = mouseEvent.clientY - rect.top - this.centerY;
    const mouseRadius = Math.sqrt(x * x + y * y);
    const mouseAngle = Math.atan2(y, x);
    const visit = (node, depth, innerRadius, startAngle, sweepAngle) => {
      const outerRadius = this.computeRadius(depth + 1);
      if (outerRadius > this.centerY)
        return void 0;
      if (mouseRadius >= innerRadius && mouseRadius < outerRadius) {
        let deltaAngle = mouseAngle - startAngle;
        deltaAngle /= Math.PI * 2;
        deltaAngle -= Math.floor(deltaAngle);
        deltaAngle *= Math.PI * 2;
        if (deltaAngle < sweepAngle) {
          if (node === this.animatedNode)
            return node.parent;
          return node;
        }
      }
      const totalBytes = node.size;
      let bytesSoFar = 0;
      for (const child of node.children) {
        const hit = visit(child, depth + 1, outerRadius, startAngle + sweepAngle * bytesSoFar / totalBytes, child.size / totalBytes * sweepAngle);
        if (hit)
          return hit;
        bytesSoFar += child.size;
      }
      return void 0;
    };
    return visit(this.animatedNode, this.animatedDepth, this.computeRadius(this.animatedDepth), this.animatedStartAngle, this.animatedSweepAngle);
  }
  tick() {
    let t = (now() - this.animationStart) / (this.options.animateDuration ?? DEFAULT_GRAPH_OPTIONS.animateDuration);
    if (t < 0 || t > 1) {
      t = 1;
      this.animatedNode = this.targetNode;
      this.targetDepth = 0;
      this.targetStartAngle = START_ANGLE;
      this.targetSweepAngle = Math.PI * 2;
    } else {
      if (t < 0.5) {
        t *= 4 * t * t;
      } else {
        t = 1 - t;
        t *= 4 * t * t;
        t = 1 - t;
      }
      this.invalidate();
    }
    this.animatedDepth = this.sourceDepth + (this.targetDepth - this.sourceDepth) * t;
    this.animatedStartAngle = this.sourceStartAngle + (this.targetStartAngle - this.sourceStartAngle) * t;
    this.animatedSweepAngle = this.sourceSweepAngle + (this.targetSweepAngle - this.sourceSweepAngle) * t;
    this.draw();
  }
  handleMouseMove(e) {
    const node = this.hitTestNode(e);
    this.changeHoveredNode(node);
    if (node && node !== this.animatedNode.parent) {
      this.events.emit("hover", node, e);
      this.canvas.style.cursor = "pointer";
    } else {
      this.events.emit("hover", null, e);
    }
  }
  computeRadius(depth) {
    return baseRadius(depth) * this.radiusScale;
  }
  updateSunburst(animate = this.options.animate ?? true) {
    if (this.previousHoveredNode !== this.hoveredNode) {
      this.previousHoveredNode = this.hoveredNode;
      if (!this.hoveredNode) {
        this.canvas.style.cursor = "auto";
        this.events.emit("hover", null);
      }
      this.invalidate();
    }
    if (this.targetNode === this.currentNode)
      return;
    this.historyStack.length = 0;
    this.invalidate();
    if (animate) {
      this.animationStart = now();
    }
    if (isParentOf(this.animatedNode, this.currentNode)) {
      const slice = {
        depth_: this.animatedDepth,
        startAngle_: this.animatedStartAngle,
        sweepAngle_: this.animatedSweepAngle
      };
      narrowSlice(this.animatedNode, this.currentNode, slice);
      this.animatedDepth = slice.depth_;
      this.animatedStartAngle = slice.startAngle_;
      this.animatedSweepAngle = slice.sweepAngle_;
      this.targetDepth = 0;
      this.targetStartAngle = START_ANGLE;
      this.targetSweepAngle = Math.PI * 2;
      this.animatedNode = this.currentNode;
    } else if (isParentOf(this.currentNode, this.animatedNode)) {
      const slice = {
        depth_: 0,
        startAngle_: START_ANGLE,
        sweepAngle_: Math.PI * 2
      };
      narrowSlice(this.currentNode, this.animatedNode, slice);
      this.targetDepth = slice.depth_;
      this.targetStartAngle = slice.startAngle_;
      this.targetSweepAngle = slice.sweepAngle_;
    } else {
      this.animationStart = -Infinity;
      this.animatedNode = this.currentNode;
    }
    this.sourceDepth = this.animatedDepth;
    this.sourceStartAngle = this.animatedStartAngle;
    this.sourceSweepAngle = this.animatedSweepAngle;
    this.targetNode = this.currentNode;
    this.events.emit("select", this.currentNode);
  }
}

const CONSTANT_PADDING = 4;
const CONSTANT_HEADER_HEIGHT = 20;
const CONSTANT_INSET_X = 2 * CONSTANT_PADDING;
const CONSTANT_INSET_Y = CONSTANT_HEADER_HEIGHT + CONSTANT_PADDING;
function layoutTreemap(sortedChildren, x, y, w, h) {
  const children = [];
  const worst = (start, end, shortestSide, totalArea, bytesToArea) => {
    const maxArea = sortedChildren[start].size * bytesToArea;
    const minArea = sortedChildren[end].size * bytesToArea;
    return Math.max(
      shortestSide * shortestSide * maxArea / (totalArea * totalArea),
      totalArea * totalArea / (shortestSide * shortestSide * minArea)
    );
  };
  const squarify = (start, x2, y2, w2, h2) => {
    while (start < sortedChildren.length) {
      let totalBytes = 0;
      for (let i = start; i < sortedChildren.length; i++) {
        totalBytes += sortedChildren[i].size;
      }
      const shortestSide = Math.min(w2, h2);
      const bytesToArea = w2 * h2 / totalBytes;
      let end = start;
      let areaInRun = 0;
      let oldWorst = 0;
      while (end < sortedChildren.length) {
        const area = sortedChildren[end].size * bytesToArea;
        const newWorst = worst(start, end, shortestSide, areaInRun + area, bytesToArea);
        if (end > start && oldWorst < newWorst)
          break;
        areaInRun += area;
        oldWorst = newWorst;
        end++;
      }
      const split = Math.round(areaInRun / shortestSide);
      let areaInLayout = 0;
      for (let i = start; i < end; i++) {
        const child = sortedChildren[i];
        const area = child.size * bytesToArea;
        const lower = Math.round(shortestSide * areaInLayout / areaInRun);
        const upper = Math.round(shortestSide * (areaInLayout + area) / areaInRun);
        const [cx, cy, cw, ch] = w2 >= h2 ? [x2, y2 + lower, split, upper - lower] : [x2 + lower, y2, upper - lower, split];
        children.push({
          node: child,
          box: [cx, cy, cw, ch],
          children: cw > CONSTANT_INSET_X && ch > CONSTANT_INSET_Y ? layoutTreemap(
            child.children,
            cx + CONSTANT_PADDING,
            cy + CONSTANT_HEADER_HEIGHT,
            cw - CONSTANT_INSET_X,
            ch - CONSTANT_INSET_Y
          ) : []
        });
        areaInLayout += area;
      }
      start = end;
      if (w2 >= h2) {
        x2 += split;
        w2 -= split;
      } else {
        y2 += split;
        h2 -= split;
      }
    }
  };
  squarify(0, x, y, w, h);
  return children;
}
class Treemap extends GraphBase {
  hoveredNode = null;
  currentNode = null;
  bgOriginX = 0;
  bgOriginY = 0;
  layers = {
    base: null
  };
  baseLayoutCache;
  currentOriginX = 0;
  currentOriginY = 0;
  animationStart = 0;
  animationBlend = 1;
  animationSource = null;
  animationTarget = null;
  constructor(tree, options = {}) {
    super(tree, options);
    this.canvas.addEventListener("mousemove", (e) => {
      this.updateHover(e);
    });
    this.canvas.addEventListener("mouseout", (e) => {
      this.changeHoveredNode(null);
      this.events.emit("hover", null, e);
    });
    this.el.addEventListener("click", (e) => {
      const layout = this.hitTestNode(e);
      if (layout) {
        const node = layout.node;
        this.events.emit("click", node, e);
        if (!node.children.length) {
          this.updateHover(e);
        } else if (layout !== this.layers.current) {
          this.changeCurrentLayout(layout);
          this.changeHoveredNode(null);
        } else {
          this.updateHover(e);
        }
      } else if (this.currentNode) {
        this.changeCurrentLayout(null);
        this.updateHover(e);
      }
    });
    this.el.append(this.canvas);
    this.resize();
    Promise.resolve().then(() => this.resize());
    this.disposables.push(useWheelEventListener((e) => this.updateHover(e)));
    this.disposables.push(useResizeEventListener(() => this.resize()));
  }
  select(node, animate) {
    let layout = null;
    if (node === this.root)
      node = null;
    if (node)
      layout = this.searchFor([this.layers.current, this.layers.base], node);
    else
      layout = null;
    this.changeCurrentLayout(layout, animate);
  }
  drawBaseLayout() {
    if (!this.width || !this.height)
      return;
    this.c.clearRect(0, 0, this.width, this.height);
    this.bgOriginX = this.bgOriginY = 0;
    if (this.baseLayoutCache) {
      this.c.putImageData(this.baseLayoutCache, 0, 0);
    } else {
      this.drawLayout(this.layers.base, 0 /* Disabled */, []);
      this.baseLayoutCache = this.c.getImageData(0, 0, this.width * this.ratio, this.height * this.ratio);
    }
  }
  draw() {
    this.drawBaseLayout();
    if (!this.layers.current)
      this.drawHoverHighlight(this.layers.base);
    const transition = !this.layers.current ? 0 : !this.animationSource ? this.animationBlend : !this.animationTarget ? 1 - this.animationBlend : 1;
    if (this.layers.current) {
      const [x, y, w, h] = this.layers.base.box;
      this.c.globalAlpha = 0.6 * (!this.layers.current || !this.animationSource ? 1 : transition);
      this.c.fillStyle = this.palette.bg;
      this.c.fillRect(x, y, w, h);
      this.c.globalAlpha = 1;
    }
    if (this.layers.previous) {
      this.drawLayout(this.layers.previous, 1 /* Enabled */, [this.layers.current]);
    }
    if (this.layers.current) {
      const [x, y, w, h] = this.layers.current.box;
      const matrix = this.c.getTransform();
      const scale = Math.sqrt(matrix.a * matrix.d);
      this.c.save();
      this.c.shadowColor = this.palette.shadow;
      this.c.shadowBlur = scale * (30 * transition);
      this.c.shadowOffsetX = scale * (2 * this.width);
      this.c.shadowOffsetY = scale * (2 * this.height + 15 * transition);
      this.c.fillRect(x - 2 * this.width, y - 2 * this.height, w, h);
      this.c.restore();
      this.bgOriginX = this.currentOriginX;
      this.bgOriginY = this.currentOriginY;
      this.drawLayout(this.layers.current, 0 /* Disabled */, []);
      this.drawHoverHighlight(this.layers.current);
    }
  }
  tick() {
    const oldAnimationBlend = this.animationBlend;
    const oldCurrentNode = this.currentNode;
    this.animationBlend = (now() - this.animationStart) / (this.options.animateDuration ?? DEFAULT_GRAPH_OPTIONS.animateDuration);
    if (this.animationBlend < 0 || this.animationBlend > 1) {
      this.currentNode = this.animationTarget;
      this.layers.previous = void 0;
      this.animationBlend = 1;
    } else {
      this.animationBlend = 1 - this.animationBlend;
      this.animationBlend *= this.animationBlend * this.animationBlend;
      this.animationBlend = 1 - this.animationBlend;
      this.invalidate();
    }
    if (this.animationBlend !== oldAnimationBlend || this.currentNode !== oldCurrentNode) {
      this.updateCurrentLayout();
    }
    this.draw();
  }
  resize() {
    this.baseLayoutCache = void 0;
    const oldWidth = this.width;
    const oldHeight = this.height;
    this.width = Math.min(this.el.clientWidth, 1600);
    this.height = Math.max(Math.round(this.width / 2), innerHeight - 200);
    if (this.width !== oldWidth || this.height !== oldHeight || !this.layers.base) {
      this.layers.base = layoutTreemap([this.root], 0, 0, this.width - 1, this.height - 1)[0];
      this.updateCurrentLayout();
    }
    super.resize();
  }
  *iterateNodeToDraw(layout, culling, cullingLayouts) {
    const node = layout.node;
    const [x, y, w, h] = layout.box;
    let flags = (node === this.hoveredNode ? 1 /* CONTAINS_HOVER */ : 0) | (layout === this.animationTarget ? 2 /* CONTAINS_TARGET */ : 0);
    if (culling === 1 /* Enabled */) {
      for (const cullingLayout of cullingLayouts) {
        if (!cullingLayout)
          continue;
        const [cx, cy, cw, ch] = cullingLayout.box;
        if (x >= cx && y >= cy && x + w <= cx + cw && y + h <= cy + ch) {
          culling = 2 /* Culled */;
          break;
        }
      }
    }
    for (const child of layout.children) {
      flags |= yield* this.iterateNodeToDraw(child, culling, cullingLayouts);
    }
    if (culling !== 2 /* Culled */) {
      yield layout;
    }
    return flags;
  }
  drawHoverHighlight(layout) {
    const iter = this.iterateNodeToDraw(layout, 0 /* Disabled */, []);
    const perviousComposite = this.c.globalCompositeOperation;
    while (true) {
      const result = iter.next();
      if (result.done) {
        this.c.globalCompositeOperation = perviousComposite;
        return;
      }
      const node = result.value.node;
      if (this.hoveredNode === node) {
        this.c.globalCompositeOperation = "overlay";
        const [x, y, w, h] = result.value.box;
        this.c.fillStyle = this.palette.hover;
        this.c.fillRect(x, y, w, h);
      }
    }
  }
  drawNodeBackground(layout, culling, cullingLayouts) {
    const iter = this.iterateNodeToDraw(layout, culling, cullingLayouts);
    while (true) {
      const result = iter.next();
      if (result.done)
        return result.value;
      const [x, y, w, h] = result.value.box;
      this.c.fillStyle = colorToCanvasFill(this.getColor(result.value.node) || this.palette.fallback, this.c, this.bgOriginX, this.bgOriginY, 1);
      if (result.value.children.length) {
        this.c.fillRect(x, y, w, CONSTANT_HEADER_HEIGHT);
        this.c.fillRect(x, y + h - CONSTANT_PADDING, w, CONSTANT_PADDING);
        this.c.fillRect(x, y + CONSTANT_HEADER_HEIGHT, CONSTANT_PADDING, h - CONSTANT_INSET_Y);
        this.c.fillRect(x + w - CONSTANT_PADDING, y + CONSTANT_HEADER_HEIGHT, CONSTANT_PADDING, h - CONSTANT_INSET_Y);
      } else {
        this.c.fillRect(x, y, w, h);
      }
    }
  }
  drawLayout(layout, culling, cullingLayouts) {
    this.drawNodeBackground(layout, culling, cullingLayouts);
    this.drawNodeForeground(layout, culling, cullingLayouts);
  }
  drawNodeForeground(layout, culling, cullingLayouts) {
    const iter = this.iterateNodeToDraw(layout, culling, cullingLayouts);
    while (true) {
      const result = iter.next();
      if (result.done)
        return;
      const node = result.value.node;
      const [x, y, w, h] = result.value.box;
      strokeRectWithFirefoxBugWorkaround(this.c, this.palette.stroke, x + 0.5, y + 0.5, w, h);
      if (h >= CONSTANT_HEADER_HEIGHT) {
        this.c.fillStyle = this.palette.text;
        const maxWidth = w - CONSTANT_INSET_X;
        const textY = y + Math.round(CONSTANT_INSET_Y / 2);
        const [nameText, nameWidth] = this.textOverflowEllipsis(this.getText(node) || "", maxWidth);
        let textX = x + Math.round((w - nameWidth) / 2);
        const text = this.getText(node);
        const subtext = this.getSubtext(node);
        if (nameText === text && node.children.length) {
          let detailText = subtext || "";
          if (detailText && text)
            detailText = ` - ${detailText}`;
          const [sizeText, sizeWidth] = this.textOverflowEllipsis(detailText, maxWidth - nameWidth);
          textX = x + Math.round((w - nameWidth - sizeWidth) / 2);
          this.c.globalAlpha = 0.5;
          this.c.fillText(sizeText, textX + nameWidth, textY);
          this.c.globalAlpha = 1;
        }
        this.c.fillText(nameText, textX, textY);
        if (h > CONSTANT_INSET_Y + 16 && !node.children.length) {
          const [sizeText, sizeWidth] = this.textOverflowEllipsis(subtext || "", maxWidth);
          this.c.globalAlpha = 0.5;
          const headerHeight = text ? CONSTANT_HEADER_HEIGHT : CONSTANT_HEADER_HEIGHT / 2 + CONSTANT_PADDING;
          this.c.fillText(sizeText, x + Math.round((w - sizeWidth) / 2), y + headerHeight + Math.round(h - CONSTANT_INSET_Y) / 2);
          this.c.globalAlpha = 1;
        }
      }
    }
  }
  updateCurrentLayout() {
    const selectedPaddingRatio = this.options.selectedPaddingRatio ?? 0.2;
    if (this.currentNode) {
      const [ox1, oy1, ow, oh] = this.currentNode.box;
      const ox2 = ox1 + ow;
      const oy2 = oy1 + oh;
      const nx1 = Math.round(this.width * selectedPaddingRatio / 2);
      const ny1 = Math.round(this.height * selectedPaddingRatio / 2);
      const nx2 = this.width - nx1 - 1;
      const ny2 = this.height - ny1 - 1;
      const t = this.animationTarget ? this.animationBlend : 1 - this.animationBlend;
      const x1 = Math.round(ox1 + (nx1 - ox1) * t);
      const y1 = Math.round(oy1 + (ny1 - oy1) * t);
      const x2 = Math.round(ox2 + (nx2 - ox2) * t);
      const y2 = Math.round(oy2 + (ny2 - oy2) * t);
      const wrap64 = (x) => x - Math.floor(x / 64 - 0.5) * 64;
      this.layers.current = layoutTreemap([this.currentNode.node], x1, y1, x2 - x1, y2 - y1)[0];
      this.currentOriginX = wrap64(-(ox1 + ox2) / 2) * (1 - t) + (x1 + x2) / 2;
      this.currentOriginY = wrap64(-(oy1 + oy2) / 2) * (1 - t) + (y1 + y2) / 2;
    } else {
      this.layers.current = void 0;
      this.currentOriginX = 0;
      this.currentOriginY = 0;
    }
  }
  hitTestNode(mouseEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const mouseX = mouseEvent.clientX - rect.left;
    const mouseY = mouseEvent.clientY - rect.top;
    const visit = (nodes, isTopLevel) => {
      for (const node of nodes) {
        const [x, y, w, h] = node.box;
        if (mouseX >= x && mouseY >= y && mouseX < x + w && mouseY < y + h) {
          return visit(node.children, false) || (isTopLevel ? null : node);
        }
      }
      return null;
    };
    return this.layers.current ? visit([this.layers.current], false) : visit([this.layers.base], true);
  }
  updateHover(e) {
    const layout = this.hitTestNode(e);
    this.changeHoveredNode(layout && layout.node);
    this.events.emit("hover", layout?.node || null, e);
  }
  changeHoveredNode(node) {
    if (this.hoveredNode !== node) {
      this.hoveredNode = node;
      this.canvas.style.cursor = node && !node.children.length ? "pointer" : "auto";
      this.invalidate();
    }
  }
  searchFor(children, node) {
    for (const child of children) {
      if (!child)
        continue;
      const result = child.node === node ? child : this.searchFor(child.children, node);
      if (result)
        return result;
    }
    return null;
  }
  changeCurrentLayout(node, animate = this.options.animate) {
    if (this.animationTarget === node)
      return;
    this.events.emit("select", node?.node || null);
    this.layers.previous = node ? this.layers.current : void 0;
    if (animate) {
      this.animationBlend = 0;
      this.animationStart = now();
      this.animationSource = this.currentNode;
    }
    this.animationTarget = node;
    this.currentNode = node || this.searchFor([this.layers.base], this.currentNode.node);
    this.updateCurrentLayout();
    this.invalidate();
  }
}

export { CONSTANT_BOLD_FONT, CONSTANT_NORMAL_FONT, DEFAULT_GRAPH_OPTIONS, DEFAULT_PALETTE, Flamegraph, GraphBase, Sunburst, Treemap, colorToCanvasFill, createColorGetterSpectrum, getTreeMaxDepth };
